// DashboardPage — главная страница с KPI cards + RecentActivity widget.
import { useQuery } from '@tanstack/react-query';
import { Box, Stack, Typography, Alert } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EventIcon from '@mui/icons-material/Event';
import ContactsIcon from '@mui/icons-material/Contacts';
import { useAuth } from '@/auth/AuthContext';
import { PageHeader } from '@/theme/PageHeader';
import { LoadingSpinner } from '@/theme/LoadingSpinner';
import { KpiCard } from './components/KpiCard';
import { RecentActivity } from './components/RecentActivity';
import { tasksApi } from '@/api/tasks';
import { dealsApi } from '@/api/deals';
import { calendarApi } from '@/api/calendar';
import { contactsApi } from '@/api/contacts';

/** Главная страница Dashboard: 4 KPI cards + recent activity. */
export function DashboardPage(): JSX.Element {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.roles.some((r) => r.code === 'admin' || r.code === 'ADMIN') ?? false;

  const tasksQuery = useQuery({
    queryKey: ['tasks-count'],
    queryFn: () => tasksApi.list({ page: 0, size: 1 }),
  });
  const dealsQuery = useQuery({
    queryKey: ['deals-count'],
    queryFn: () => dealsApi.list({ page: 0, size: 1 }),
  });
  const eventsQuery = useQuery({
    queryKey: ['events-today-count'],
    queryFn: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
      return calendarApi.list({ from: start, to: end, size: 100 });
    },
  });
  const contactsQuery = useQuery({
    queryKey: ['contacts-count'],
    queryFn: () => contactsApi.list({ page: 0, size: 1 }),
  });

  const isLoading =
    tasksQuery.isLoading ||
    dealsQuery.isLoading ||
    eventsQuery.isLoading ||
    contactsQuery.isLoading;
  const hasError =
    tasksQuery.isError ||
    dealsQuery.isError ||
    eventsQuery.isError ||
    contactsQuery.isError;

  return (
    <Box>
      <PageHeader
        title={`Добро пожаловать${currentUser ? `, ${currentUser.username}` : ''}!`}
        subtitle="Сводка по системе Упряжка"
      />
      {hasError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Не все данные удалось загрузить. Попробуйте позже.
        </Alert>
      ) : null}
      {isLoading && !hasError ? (
        <LoadingSpinner label="Загрузка KPI…" />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <KpiCard
            label="Задачи"
            value={tasksQuery.data?.total ?? 0}
            icon={<AssignmentIcon />}
            iconColor="primary"
          />
          <KpiCard
            label="Сделки"
            value={dealsQuery.data?.total ?? 0}
            icon={<TrendingUpIcon />}
            iconColor="success"
          />
          <KpiCard
            label="События сегодня"
            value={eventsQuery.data?.total ?? 0}
            icon={<EventIcon />}
            iconColor="tertiary"
          />
          <KpiCard
            label="Контакты"
            value={contactsQuery.data?.total ?? 0}
            icon={<ContactsIcon />}
            iconColor="secondary"
          />
        </Box>
      )}
      <Stack spacing={2}>
        <RecentActivity showAuditEvents={isAdmin} />
        {isAdmin ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Вы вошли как администратор. Доступны audit-events API и расширенные действия.
            </Typography>
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
}
